import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Sparkles, Volume2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

function speak(text, lang) {
  if (!("speechSynthesis" in window)) return;
  const langMap = {
    spanish: "es-ES", french: "fr-FR", german: "de-DE",
    italian: "it-IT", japanese: "ja-JP", portuguese: "pt-PT",
    korean: "ko-KR", mandarin: "zh-CN", english: "en-US",
  };
  const u = new SpeechSynthesisUtterance(text);
  u.lang = langMap[lang] || "en-US";
  u.rate = 0.85;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

// Small hero card that shows *today's* word right at the top of the calendar.
// Deliberately punchy — this is the "excitement engine" of the app.
export default function TodayHero({ language, langLabel, isCompleted, onOpen }) {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const todayIso = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/lessons/${language}/${todayIso}`)
      .then((r) => { if (alive) setLesson(r.data.lesson); })
      .catch(() => { if (alive) setLesson(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [language, todayIso]);

  const readableDate = new Date(todayIso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8"
      data-testid="today-hero"
    >
      {/* Decorative honey glow */}
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-honey/20 blur-3xl pointer-events-none" />
      <div className="absolute -left-10 -bottom-16 w-48 h-48 rounded-full bg-terracotta/20 blur-3xl pointer-events-none" />

      <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-secondary-foreground/70">
            <Sparkles className="w-3.5 h-3.5 text-honey" strokeWidth={2} />
            {readableDate} &middot; today&rsquo;s {langLabel} word
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-3 text-secondary-foreground/70">
              <Loader2 className="w-5 h-5 animate-spin" /> Cooking up your word&hellip;
            </div>
          ) : lesson ? (
            <>
              <div className="flex items-baseline gap-4 flex-wrap">
                <h2
                  className="font-display text-5xl sm:text-6xl tracking-tight text-honey leading-none"
                  data-testid="today-hero-word"
                >
                  {lesson.word}
                </h2>
                <span className="text-lg text-secondary-foreground/80" data-testid="today-hero-translation">
                  &rarr; {lesson.translation}
                </span>
              </div>
              <p className="text-sm text-secondary-foreground/70 italic max-w-xl">
                &ldquo;{lesson.example_sentence}&rdquo; &mdash; {lesson.example_translation}
              </p>
              <p className="text-xs text-secondary-foreground/50">
                /{lesson.pronunciation}/ &middot; {lesson.part_of_speech}
              </p>
            </>
          ) : (
            <div className="text-secondary-foreground/70">Couldn&rsquo;t load today&rsquo;s word.</div>
          )}
        </div>

        <div className="flex md:flex-col gap-2 md:items-end">
          <Button
            onClick={() => lesson && speak(lesson.word, language)}
            variant="outline"
            data-testid="today-hero-pronounce"
            className="rounded-full h-10 bg-transparent border-honey/50 text-honey hover:bg-honey/10 hover:text-honey"
          >
            <Volume2 className="w-4 h-4 mr-2" strokeWidth={1.75} /> Hear it
          </Button>
          <Button
            onClick={onOpen}
            data-testid="today-hero-open"
            className="rounded-full h-10 bg-terracotta hover:bg-terracotta/90 text-white"
          >
            {isCompleted ? "See today's lesson" : "Take today's lesson"}
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      </div>
    </div>
  );
}
